import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "../store/redux/slices/authSlice";

const INACTIVITY_LIMIT_MS = 30 * 60 * 1000; // 30 minutes
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];

// Watches for user activity anywhere in the authenticated app and, after a
// minute of silence, shows a modal the user must acknowledge before being
// signed out. Renders nothing while logged out.
const InactivityLogout = () => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isTimedOut, setIsTimedOut] = useState(false);
  const timerRef = useRef(null);

  const armTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setIsTimedOut(true), INACTIVITY_LIMIT_MS);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || isTimedOut) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return undefined;
    }

    armTimer();
    ACTIVITY_EVENTS.forEach((eventName) => window.addEventListener(eventName, armTimer));

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => window.removeEventListener(eventName, armTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isAuthenticated, isTimedOut, armTimer]);

  const handleAcknowledge = () => {
    setIsTimedOut(false);
    dispatch(logout());
    navigate("/login");
  };

  if (!isAuthenticated || !isTimedOut) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Session timeout"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999999,
        background: "rgba(15, 23, 42, 0.65)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.35)",
          width: "100%",
          maxWidth: "420px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: "linear-gradient(135deg, #ee5a52 0%, #dc3545 50%, #b02a37 100%)",
            color: "#fff",
            padding: "18px 24px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: "20px" }}></i>
          <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700 }}>Session Timeout</h2>
        </div>

        <div style={{ padding: "24px" }}>
          <p style={{ margin: 0, color: "#263238", fontSize: "14.5px", lineHeight: 1.6 }}>
            You've been inactive for a while, so for your security we've ended this session.
            Please log in again to continue.
          </p>
        </div>

        <div style={{ padding: "0 24px 24px", display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={handleAcknowledge}
            style={{
              background: "linear-gradient(135deg, #ee5a52 0%, #dc3545 100%)",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              padding: "10px 28px",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "0 8px 20px rgba(220, 53, 69, 0.4)",
            }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default InactivityLogout;
